import { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import { a, useSpring } from "@react-spring/three";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { addEventListener, emitEvent } from "../src/events/eventBus";
import { useFrame } from "@react-three/fiber";

export default function Cake({
  initialPosition = [0, 0, 0],
  targetPosition = [0, 1, 0],
  rotation = [0, 0, 0],
  scale = 1,
  raiseDelay = 0.3,
  raiseDuration = 0.2,
  ...props
}) {
  const group = useRef();
  const { scene, animations } = useGLTF("/cake.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions, names } = useAnimations(animations, group);
  const hasRaisedRef = useRef(false);
  const liftCompletedRef = useRef(false);
  const [initialX, initialY, initialZ] = initialPosition;
  const [targetX, targetY, targetZ] = targetPosition;
  const rotationSpeed = useMemo(() => new THREE.Vector3(0, -Math.PI / 16, 0), []);

  const initialVector = useMemo(() => [initialX, initialY, initialZ], [initialX, initialY, initialZ]);
  const targetVector = useMemo(() => [targetX, targetY, targetZ], [targetX, targetY, targetZ]);

  const [isRaised, setIsRaised] = useState(false);

  const springs = useSpring({
    position: isRaised ? targetVector : initialVector,
    config: { duration: isRaised ? raiseDuration * 1000 : 0 },
    immediate: !isRaised,
    delay: isRaised ? raiseDelay * 1000 : 0,
    onRest: () => {
      if (!hasRaisedRef.current || !isRaised || liftCompletedRef.current) return;
      liftCompletedRef.current = true;
      emitEvent("cakeLiftComplete", { position: [...targetVector] });
    },
  });

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    hasRaisedRef.current = isRaised;
  }, [isRaised]);

  useEffect(() => {
    if (!actions) return undefined;

    const actionName = names?.[0] ?? Object.keys(actions)[0];
    if (!actionName) return undefined;

    const action = actions[actionName];
    if (!action) return undefined;

    action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.2).play();
    action.clampWhenFinished = false;

    return () => {
      action.fadeOut(0.2);
      action.stop();
    };
  }, [actions, names]);

  useEffect(() => {
    const unsubscribe = addEventListener("playCake", () => {
      if (hasRaisedRef.current) return;
      hasRaisedRef.current = true;
      liftCompletedRef.current = false;
      setIsRaised(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!group.current) return;
    group.current.rotation.set(rotation[0], rotation[1], rotation[2]);
  }, [rotation]);

  useFrame((_, delta) => {
    const node = group.current;
    if (!node) return;
    node.rotation.y += rotationSpeed.y * delta;
  });

  return (
    <a.group ref={group} position={springs.position} rotation={rotation} scale={scale} {...props}>
      <primitive object={clonedScene} />
    </a.group>
  );
}

useGLTF.preload("/cake.glb");
